// Apolo IDE — SSE client (fetch + ReadableStream).
//
// Used by useStreamingChat. Reads a server-sent event stream and emits
// each `data:` payload (parsed JSON) to `onChunk`. Resolves on `[DONE]`
// or upstream `type: 'done'` events. Supports abort via AbortController.

import { apiUrl } from './apiBase.js';
import { getStoredToken } from './api.js';

const DONE_TOKEN = '[DONE]';

/**
 * Open an SSE POST stream.
 * @param {string} path
 * @param {object} options
 * @param {object} options.body       JSON body
 * @param {(chunk: object) => void} options.onChunk
 * @param {AbortSignal=} options.signal
 * @param {string=} options.token
 * @returns {Promise<{ ok: boolean, error?: string, status: number }>}
 */
export async function streamSSE(path, { body, onChunk, signal, token } = {}) {
  const auth = token ?? getStoredToken();
  let res;
  try {
    res = await fetch(apiUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(auth ? { Authorization: `Bearer ${auth}` } : {}),
      },
      body: JSON.stringify(body ?? {}),
      signal,
    });
  } catch (e) {
    return { ok: false, error: e?.message || 'network error', status: 0 };
  }

  if (!res.ok) {
    let errMessage = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      errMessage = body.error || errMessage;
    } catch {
      // ignore parse errors — fall through
    }
    return { ok: false, error: errMessage, status: res.status };
  }

  const reader = res.body?.getReader();
  if (!reader) return { ok: false, error: 'no response body', status: res.status };

  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    if (signal?.aborted) {
      try {
        reader.cancel();
      } catch {
        // ignore — already cancelled
      }
      return { ok: false, error: 'aborted', status: res.status };
    }
    let chunk;
    try {
      chunk = await reader.read();
    } catch (e) {
      return { ok: false, error: e?.message || 'stream read error', status: res.status };
    }
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });

    let newlineIdx;
    while ((newlineIdx = buffer.indexOf('\n\n')) !== -1) {
      const event = buffer.slice(0, newlineIdx);
      buffer = buffer.slice(newlineIdx + 2);
      const dataLines = event
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trim());
      if (dataLines.length === 0) continue;
      const raw = dataLines.join('\n');
      if (raw === DONE_TOKEN) {
        return { ok: true, status: res.status };
      }
      try {
        onChunk?.(JSON.parse(raw));
      } catch {
        onChunk?.({ raw });
      }
    }
  }
  return { ok: true, status: res.status };
}
